import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { revealSection } from "../../common/scroll";
import { scrollToEventTabs } from "./eventTabsAnchor";
import { REVEAL_PARAM } from "./revealAnchor";

const GIVE_UP_AFTER_MS = 4000;

export const useRevealAnchor = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get(REVEAL_PARAM);

  // react-router 6.30 hands setSearchParams the snapshot from the render that created it — the
  // functional form included — so an async write has to read the params back out of a ref.
  // Assigned in render, not an effect: React flushes those after the observer's microtask below.
  const live = useRef({ searchParams, setSearchParams });
  live.current = { searchParams, setSearchParams };

  useEffect(() => {
    if (requested == null) return;

    const clearParam = () => {
      const { searchParams: current, setSearchParams: write } = live.current;
      const next = new URLSearchParams(current);
      next.delete(REVEAL_PARAM);
      write(next, { replace: true });
    };

    // The observer fires mid-mutation, so let the tab finish painting before scrolling.
    const reveal = (section: HTMLElement) => requestAnimationFrame(() => revealSection(section));

    const present = document.getElementById(requested);
    if (present != null) {
      reveal(present);
      clearParam();
      return;
    }

    let settled = false;
    const finish = (section: HTMLElement | null) => {
      if (settled) return;
      settled = true;
      if (section != null) reveal(section);
      else scrollToEventTabs();
      clearParam();
    };

    const observer = new MutationObserver(() => {
      const section = document.getElementById(requested);
      if (section != null) finish(section);
    });
    const timer = setTimeout(() => finish(null), GIVE_UP_AFTER_MS);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [requested]);
};
